import React, { useState } from "react"
import pluralize from "pluralize"
import {
  Box, Divider, Flex, Skeleton, Stack, Text,
} from "@chakra-ui/react"
import {
  DioboxEvent, Form, FormType, Table, TableShapes,
} from "sharedTypes"
import { useForms } from "queries/forms"
import { eventFormsPath, mobileAppURL } from "utilities/routes"
import { asMoney } from "utilities/strings"
import SegmentedControl from "../components/SegmentedControl"
import DashboardCard, { EmptyState, LinkAction } from "../components/DashboardCard"
import {
  Bar, BigStat, Donut, Legend, ProgressRows, Segment, StackedBar, TICKETING_COLOR,
} from "../components/charts"

type Props = { event: DioboxEvent }

type WithEmptyAction = Props & { onEmptyAction?: () => void }

export const GuestsCard = ({ event: { counts }, onEmptyAction }: WithEmptyAction) => {
  if (!counts.guests) {
    return (
      <DashboardCard title="Guests">
        <EmptyState
          title="No guests yet"
          body="Add guests one by one or import a spreadsheet."
          cta="Import guests"
          onClick={onEmptyAction}
        />
      </DashboardCard>
    )
  }

  const statuses: Segment[] = [
    { label: "Confirmed", value: counts.confirmedGuests, color: "status.confirmed" },
    { label: "Waitlisted", value: counts.waitlistedGuests, color: "status.waitlisted" },
    { label: "Declined", value: counts.declinedGuests, color: "status.declined" },
    { label: "Unconfirmed", value: counts.unconfirmedGuests, color: "status.unconfirmed" },
  ]

  return (
    <DashboardCard title="Guests">
      <Stack spacing={6} align="center">
        <Donut items={statuses} centerValue={counts.guests.toLocaleString()} centerLabel="Total guests" />
        <Stack w="full">
          <Legend items={statuses} total={counts.guests} percentFirst squares />
        </Stack>
      </Stack>
    </DashboardCard>
  )
}

// Without check-in sessions there's nothing to set up beyond the mobile app.
export const AttendanceCard = (
  { event: { counts, checkInLocationsEnabled }, onEmptyAction }: WithEmptyAction,
) => {
  const [base, setBase] = useState<"all" | "confirmed">("all")

  if (!counts.arrivedGuests) {
    return (
      <DashboardCard title="Attendance">
        <EmptyState
          title="No check-ins yet"
          body="Check-ins appear here once doors open."
          cta={checkInLocationsEnabled ? "Set up check-in" : "Download mobile app"}
          onClick={checkInLocationsEnabled
            ? onEmptyAction
            : () => window.open(mobileAppURL(), "_blank", "noopener")}
        />
      </DashboardCard>
    )
  }

  const total = base === "confirmed" ? counts.confirmedGuests : counts.guests
  const arrived = counts.arrivedGuests

  return (
    <DashboardCard
      title="Attendance"
      action={(
        <SegmentedControl
          value={base}
          onChange={setBase}
          options={[{ value: "all", label: "All" }, { value: "confirmed", label: "Confirmed" }]}
        />
      )}
    >
      <BigStat label={base === "confirmed" ? "Confirmed guests" : "Total guests"} value={total} />
      <Stack spacing={0} mt={4}>
        <ProgressRows
          total={total}
          rows={[
            { label: "Checked In", value: arrived, color: "status.checkedIn" },
            { label: "Not Checked In", value: Math.max(0, total - arrived), color: "status.notCheckedIn" },
          ]}
        />
      </Stack>
    </DashboardCard>
  )
}

export const EmailsCard = ({ event: { counts }, onEmptyAction }: WithEmptyAction) => {
  if (!counts.emails) {
    return (
      <DashboardCard title="Emails">
        <EmptyState
          title="No emails sent"
          body="Send an invitation or save-the-date to see opens and clicks."
          cta="New email"
          onClick={onEmptyAction}
        />
      </DashboardCard>
    )
  }

  const sent = counts.emails
  const rows: Segment[] = [
    { label: "Opened", value: counts.openedEmails, color: "emailStatus.opened" },
    { label: "Clicked", value: counts.clickedEmails, color: "emailStatus.clicked" },
    { label: "Unopened", value: sent - counts.openedEmails, color: "emailStatus.unopened" },
    { label: "Soft bounced", value: counts.softBouncedEmails, color: "emailStatus.softBounced" },
    { label: "Hard bounced", value: counts.hardBouncedEmails, color: "emailStatus.hardBounced" },
  ]

  return (
    <DashboardCard title="Emails">
      <BigStat label="Total emails sent" value={sent} />
      <Stack spacing={0} mt={4}>
        <ProgressRows rows={rows} total={sent} />
      </Stack>
    </DashboardCard>
  )
}

const GuestCountRow = ({ label, count }: { label: string, count: number }) => (
  <Flex justify="space-between">
    <Text color="gray.700">{label}</Text>
    <Text fontWeight="semibold">
      {count.toLocaleString()}
      <Text as="span" color="gray.600" fontWeight="normal"> {pluralize("guest", count)}</Text>
    </Text>
  </Flex>
)

const tablesLabel = (tables: Table[]) => {
  const rows = tables.filter(({ shape }) => shape === TableShapes.Row).length

  if (!rows) {
    return "Total tables"
  }

  return rows === tables.length ? "Total rows" : "Total tables and rows"
}

export const SeatingCard = ({ event: { counts, tables = [] }, onEmptyAction }: WithEmptyAction) => {
  if (!counts.seats) {
    return (
      <DashboardCard title="Seating">
        <EmptyState
          title="No seating plan"
          body="Add tables or rows to assign guests to seats."
          cta="Build seating plan"
          onClick={onEmptyAction}
        />
      </DashboardCard>
    )
  }

  const split: Segment[] = [
    { label: "Assigned", value: counts.assignedSeats, color: "blue.500" },
    { label: "Vacant", value: counts.unassignedSeats, color: "gray.200" },
  ]

  return (
    <DashboardCard title="Seating">
      <Stack spacing={2}>
        <BigStat label={tablesLabel(tables)} value={tables.length} />
        <BigStat label="Total seats" value={counts.seats} />
      </Stack>
      <Divider my={4} />
      <Stack spacing={3}>
        <StackedBar items={split} />
        <Legend items={split} total={counts.seats} />
      </Stack>
      <Divider my={4} />
      <Stack spacing={2}>
        {counts.standingGuests !== undefined && (
          <GuestCountRow label="Standing" count={counts.standingGuests} />
        )}
        <GuestCountRow label="Waitlisted" count={counts.waitlistedSeats} />
      </Stack>
    </DashboardCard>
  )
}

const SHOWN_TICKET_TYPES = 5

const ticketPrice = ({ price, variablePrice }: Form, currency: string) => {
  if (variablePrice) {
    return null
  }
  if (!Number(price)) {
    return "Free"
  }

  return asMoney(price, currency, Number(price) % 1 === 0)
}

const SoldCount = ({ sold, quantity }: { sold: number, quantity?: number | null }) => (
  <Text fontWeight="semibold" flexShrink={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
    {sold.toLocaleString()}
    {quantity ? (
      <Text as="span" color="gray.600" fontWeight="normal"> / {quantity.toLocaleString()}</Text>
    ) : null}
  </Text>
)

const TicketTypeRow = ({ form, currency }: { form: Form, currency: string }) => {
  const price = ticketPrice(form, currency)

  return (
    <Box pt={3}>
      <Flex justify="space-between" mb={1.5} gap={3}>
        <Flex minW={0} gap={1}>
          <Text color="gray.700" noOfLines={1}>{form.name}</Text>
          {price && <Text color="gray.600" flexShrink={0}>· {price}</Text>}
        </Flex>
        <SoldCount sold={form.submissionLineItemsCount} quantity={form.quantity} />
      </Flex>
      {form.quantity ? (
        <Bar pct={(form.submissionLineItemsCount / form.quantity) * 100} color={TICKETING_COLOR} />
      ) : null}
    </Box>
  )
}

export const TicketingCard = ({
  event: { id, counts, currency }, onEmptyAction,
}: WithEmptyAction) => {
  const { data, isLoading } = useForms(id)
  const ticketTypes = (data?.data ?? []).filter(({ type, disabled, submissionLineItemsCount }) => (
    type === FormType.Ticket && (!disabled || submissionLineItemsCount > 0)
  ))
  const allLoaded = Boolean(data) && !data?.meta?.nextPage
  const limited = ticketTypes.length > 0 && ticketTypes.every(({ quantity }) => quantity)
  const capacity = allLoaded && limited
    ? ticketTypes.reduce((sum, { quantity }) => sum + Number(quantity), 0)
    : null

  if (!counts.formSubmissionLineItems.ticket && !Number(counts.grossSales)) {
    return (
      <DashboardCard title="Ticketing">
        <EmptyState
          title="No tickets sold"
          body="Add ticket types to a registration form to start selling."
          cta="Set up ticketing"
          onClick={onEmptyAction}
        />
      </DashboardCard>
    )
  }

  return (
    <DashboardCard title="Ticketing">
      <Flex gap={6}>
        <Stack spacing={0.5} flex={1}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">Net sales</Text>
          <Text fontSize="2xl" fontWeight="semibold">{asMoney(counts.netSales, currency)}</Text>
        </Stack>
        <Stack spacing={0.5} flex={1}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">Gross sales</Text>
          <Text fontSize="2xl" fontWeight="semibold">{asMoney(counts.grossSales, currency)}</Text>
        </Stack>
      </Flex>
      <Divider my={4} />
      <Flex justify="space-between">
        <Text color="gray.600" fontWeight="semibold">Tickets sold</Text>
        <SoldCount sold={counts.formSubmissionLineItems.ticket ?? 0} quantity={capacity} />
      </Flex>
      {isLoading && (
        <Stack spacing={3} pt={3} aria-label="Loading ticket types" aria-busy>
          <Skeleton h={4} />
          <Skeleton h={4} />
        </Stack>
      )}
      {ticketTypes.slice(0, SHOWN_TICKET_TYPES).map((form) => (
        <TicketTypeRow key={form.id} form={form} currency={currency} />
      ))}
      {ticketTypes.length > SHOWN_TICKET_TYPES && (
        <Box pt={4}>
          <LinkAction to={eventFormsPath(id)} chevron>
            View all {ticketTypes.length} ticket types
          </LinkAction>
        </Box>
      )}
    </DashboardCard>
  )
}
